import { useEffect, useState } from 'react'
import { useQuery } from 'urql'
import { useWorkspace } from 'app/utils'
import { WORKSPACE_THROTTLE_QUERY } from 'app/lib/workflows/graphql'
import {
  type GlobalWorkflowSettings,
  settingsFromWorkspace,
  type WorkspaceThrottleFields,
} from 'app/lib/workflows/globalSettings'

/** The workspace-wide throttle contract (TRK-5385), loaded fresh so the UI reflects what the engine
 *  will actually enforce. Shared by the list page's header pill/settings modal and the activation
 *  preflight. `setSettings` lets the Automation settings modal reflect a save without a refetch. */
export function useWorkspaceThrottle(): {
  workspaceId: string | undefined
  settings: GlobalWorkflowSettings
  setSettings: (settings: GlobalWorkflowSettings) => void
} {
  const workspaceId = useWorkspace()?.id
  const [{ data }] = useQuery<{ workspace?: WorkspaceThrottleFields }>({
    query: WORKSPACE_THROTTLE_QUERY,
    variables: { id: workspaceId },
    pause: !workspaceId,
    requestPolicy: `network-only`,
  })

  // Local state so the settings modal can reflect its own save immediately; the query result seeds
  // it and wins whenever the server answers again.
  const [settings, setSettings] = useState<GlobalWorkflowSettings>({})
  useEffect(() => {
    setSettings(workspaceId ? settingsFromWorkspace(data?.workspace) : {})
  }, [workspaceId, data])

  return { workspaceId, settings, setSettings }
}
